import { useCallback, useEffect, useState, useRef } from 'react';
import './toaster.css';
import { Bounce, ToastContainer, toast } from 'react-toastify';
import { Button, Icon, Typography, useInterval } from '@packages/ui/shared';
import { CvtTable } from '../components/cvt-table';
import { CVTQuickFilters } from '../components/cvt-filters';
import { analyticsService, cvtService } from '@web/services/singletons';
import { CvtPrimaryConcernsModal } from '../components/cvt-primary-concerns-modal';
import { ErrorDetail } from '../components/error-detail';
import { GetCVTParams, toQueryString, useCvt } from '@packages/contexts/cvt';
import { CVT_STATUS_MAP } from '@packages/models/api/cvt';
import { includes, keys } from 'lodash';
import { Modal } from 'react-bootstrap';
import { useContentOverlayState, useQuery } from '@web/core/hooks';
import { useCvtDiagnostic } from '../hook/use-cvt-diagnostic';
import { useCvtDiagnosticReport } from '../hook/use-cvt-diagnostic-report';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpdateQueryParams } from '../hook/useQueryParams';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { DATE_FORMAT } from '@web/config/constants';
import { DateTime } from 'luxon';
import ToastNotification from '@web/components/toast-notification';
import { AlertBar } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

const logger = getLogger('cvt');

const getKeysByStatus = (statuses: string[]): string[] => {
    return keys(CVT_STATUS_MAP).filter((key) => includes(statuses, CVT_STATUS_MAP[key as keyof typeof CVT_STATUS_MAP]));
};

const CVTPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryParams = useQuery();
    const updateQuery = useUpdateQueryParams();
    const { toggledOverlays, toggleOverlay } = useContentOverlayState();
    const { selectedVin } = useVinSessionState();
    const { fetchCvtList, state: cvtState, clearErrors } = useCvt(cvtService);
    const { downloadingIds, downloadReport } = useCvtDiagnosticReport();
    const { isUploading, runDiagnostic } = useCvtDiagnostic();
    const [showToast, setShowToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const isFetchingRef = useRef(false);
    const [pendingCvts, setPendingCvts] = useState<Set<string>>(new Set());
    const [showPrimaryConcernsModal, setShowPrimaryConcernsModal] = useState(false);
    const [params, setParams] = useState<GetCVTParams>({});
    const [alertBarMessage, setAlertBarMessage] = useState<{ message: string; type?: 'success' | 'error' }>({
        message: '',
    });
    const { errors } = cvtState;

    const canRunDiagnostic = Boolean(selectedVin?.cvtEligible);
    const vin = selectedVin?.vinRecord?.vin;

    const sortBy = queryParams.get('sortBy') || '';
    const sortDirection = queryParams.get('sortDirection') || '';
    const statusesFilter = queryParams.getAll('statuses');
    const startDate = queryParams.get('startDate') || undefined;
    const endDate = queryParams.get('endDate') || undefined;
    const page = queryParams.get('page');
    const size = queryParams.get('size');

    const hasQuickFilters = statusesFilter.length > 0 || !!startDate || !!endDate;

    const toggleQuickFilterPanel = useCallback(() => {
        toggleOverlay('escalationQuickFilters');
    }, [toggleOverlay]);

    const clearFilters = () => {
        updateQuery({ statuses: null, startDate: null, endDate: null });
    };

    const buildParamsFromQuery = (): GetCVTParams => {
        const result: GetCVTParams = {};
        if (sortBy) result.sortBy = sortBy;
        if (sortDirection) result.sortDirection = sortDirection;
        if (startDate) result.startDate = startDate;
        if (endDate) result.endDate = endDate;
        if (statusesFilter.length) result.statuses = getKeysByStatus(statusesFilter);
        if (page) result.page = Number(page);
        if (size) result.size = Number(size);
        return result;
    };

    const handleDownloadReport = useCallback(
        async (diagnosticId: string) => {
            const result = await downloadReport(diagnosticId);
            if (result) {
                setShowToast({ show: true, message: 'The CVT diagnostic document has been successfully downloaded.' });
            }
        },
        [downloadReport]
    );

    useEffect(() => {
        const run = async () => {
            const newParams = buildParamsFromQuery();
            setParams(newParams);

            if (vin && !isFetchingRef.current) {
                isFetchingRef.current = true;
            }
            try {
                await fetchCvtList(vin, newParams);
            } finally {
                isFetchingRef.current = false;
            }
        };

        run();
    }, [sortBy, sortDirection, startDate, endDate, statusesFilter.join(','), page, size, vin]);

    function handlePrimaryConcernsModalClose() {
        setShowPrimaryConcernsModal(false);
    }

    async function handleRunDiagnostic(file: File, repairOrderNumber: string) {
        const { stsDiagnosticId: diagnosticId } = await runDiagnostic(file, vin, repairOrderNumber);

        setShowPrimaryConcernsModal(false);

        analyticsService.logEvent(ANALYTICS_EVENTS.CVT_DIAGNOSTIC_SUBMITTED, {
            moduleName: 'cvt',
            diagnosticId,
        });

        if (!vin) {
            return;
        }

        try {
            await fetchCvtList(vin, params);
        } catch (error) {
            toast.error(error.message || 'An error occurred while fetching the CVT list.');
        }

        try {
            const response = await cvtService.getCvtStatus(diagnosticId);
            if ('data' in response && 'status' in response.data) {
                const stsErrorResponse = response?.data?.stsErrorResponse;
                const status = response?.data?.status;

                if (stsErrorResponse && status !== 'SBR_UPLOAD_FAIL') {
                    analyticsService.logEvent(ANALYTICS_EVENTS.CVT_RESULT_RECEIVED, {
                        moduleName: 'cvt',
                        diagnosticId,
                        resultStatus: 'error',
                        status,
                        errorMessage: stsErrorResponse,
                    });
                    toast.error(stsErrorResponse, { autoClose: false, closeButton: true });
                    try {
                        await fetchCvtList(vin, params, true);
                    } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : 'Failed to update CVT list.';
                        analyticsService.logEvent(ANALYTICS_EVENTS.CVT_RESULT_RECEIVED, {
                            moduleName: 'cvt',
                            diagnosticId,
                            resultStatus: 'error',
                            status,
                            errorMessage,
                        });
                        toast.error(errorMessage || 'An error occurred while updating the CVT list.');
                    }
                    return;
                }

                //CVT_RESULT_RECEIVED with success
                analyticsService.logEvent(ANALYTICS_EVENTS.CVT_RESULT_RECEIVED, {
                    moduleName: 'cvt',
                    diagnosticId,
                    resultStatus: 'success',
                    status,
                });
                setPendingCvts((prev) => new Set(prev).add(diagnosticId));
            }
        } catch (error) {
            //CVT_RESULT_RECEIVED with error
            const errorMessage = error instanceof Error ? error.message : 'Failed to check CVT diagnostic status.';
            analyticsService.logEvent(ANALYTICS_EVENTS.CVT_RESULT_RECEIVED, {
                moduleName: 'cvt',
                diagnosticId,
                resultStatus: 'error',
                errorMessage,
            });
            logger.error(`Failed to check the CVT diagnostic status [${diagnosticId}]`, { error, diagnosticId, vin });
        }
    }

    function handleGoToStis() {
        navigate(`/vin/${vin}/stis`);
    }

    useInterval(async () => {
        if (!pendingCvts.size) {
            return;
        }

        const statusChecks = Array.from(pendingCvts).map(async (diagnosticId) => {
            try {
                const response = await cvtService.getCvtStatus(diagnosticId);

                if ('data' in response && 'status' in response.data) {
                    const { status } = response.data;
                    const statusTransformed = CVT_STATUS_MAP[status];

                    if (statusTransformed.toLowerCase() !== 'pending') {
                        setPendingCvts((prev) => {
                            const newSet = new Set(prev);
                            const wasDeleted = newSet.delete(diagnosticId);

                            if (wasDeleted) {
                                fetchCvtList(vin, params, true);
                                if (
                                    status !== 'SBR_UPLOAD_FAIL' &&
                                    'stsErrorResponse' in response.data &&
                                    response.data.stsErrorResponse
                                ) {
                                    toast.error(response.data.stsErrorResponse, {
                                        autoClose: false,
                                        closeButton: true,
                                    });
                                }
                                if (
                                    'sbrResponseData' in response.data &&
                                    response.data.sbrResponseData &&
                                    'errorDetail' in response.data.sbrResponseData
                                ) {
                                    const alertType =
                                        response.data.sbrResponseData.errorType === 'NoResult' ? 'success' : 'error';
                                    setAlertBarMessage({
                                        message: response.data.sbrResponseData.errorDetail,
                                        type: alertType,
                                    });
                                }
                            }
                            return newSet;
                        });
                    }
                }
            } catch (e) {
                const message = e instanceof Error ? e.message : 'Error checking diagnostic status';
                logger.error(`Failed to check the CVT diagnostic status [${diagnosticId}]`, {
                    error: e,
                    diagnosticId,
                    vin,
                });
                throw e instanceof Error ? e : new Error(message);
            }
        });
        await Promise.all(statusChecks);
    }, 5000);

    useEffect(() => {
        if (vin) {
            const fetchPendingList = async () => {
                const today = DateTime.now().toFormat(DATE_FORMAT.YYYY_MM_DD);
                const query = toQueryString({
                    statuses: getKeysByStatus(['Pending']),
                    startDate: today,
                    endDate: today,
                });
                const pendingList = await cvtService.getCvtList(vin, query);

                if ('data' in pendingList && 'results' in pendingList.data) {
                    if (pendingList.data.results.length) {
                        setPendingCvts(new Set(pendingList.data.results.map((cvt) => cvt.diagnosticId)));
                    }
                }
            };
            fetchPendingList();
        }
    }, [vin]);

    return (
        <>
            <Modal show={errors.length > 0} centered onHide={clearErrors}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('cvt:title', 'CVT Diagnostic')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Typography>
                        {t('cvt:error.message', 'Unable to retrieve diagnostic data. Please try again later.')}
                    </Typography>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onPress={clearErrors}>
                        {t('common:close', 'Close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <CvtPrimaryConcernsModal
                isUploading={isUploading}
                onClose={handlePrimaryConcernsModalClose}
                onGoToStis={handleGoToStis}
                onRunDiagnostic={handleRunDiagnostic}
                show={showPrimaryConcernsModal}
            />

            <AlertBar
                show={!!alertBarMessage.message}
                success={alertBarMessage.type === 'success'}
                error={alertBarMessage.type === 'error'}
                onClose={() => setAlertBarMessage({ message: '' })}
                style={{ alignItems: 'center' }}
            >
                <ErrorDetail errorDetail={alertBarMessage.message} style={{ fontSize: '15px', margin: 0 }} />
            </AlertBar>

            <div className="pt-7 pl-7 pr-7 d-flex h-100">
                <div className="d-flex w-100 flex-column">
                    <div className="mb-6 d-flex justify-content-between">
                        <Typography variant="h2">{t('cvt:title', 'CVT Diagnostic')}</Typography>
                        <Button
                            disabled={!canRunDiagnostic}
                            onPress={() => {
                                setShowPrimaryConcernsModal(true);
                                analyticsService.logEvent(ANALYTICS_EVENTS.CVT_TOOL_ACCESSED, {
                                    moduleName: 'cvt',
                                    sourceModule: 'vin_session',
                                });
                            }}
                        >
                            {t('cvt:button.run', 'Run Diagnostic')}
                        </Button>
                    </div>

                    <div className="mb-4 d-flex">
                        <Button
                            variant="ghost-blue"
                            iconLeft={<Icon color="blueOne" name="filter-list" />}
                            onPress={toggleQuickFilterPanel}
                        >
                            {hasQuickFilters
                                ? t('filters.applied', 'Filters applied')
                                : t('filters.default', 'Filters')}
                        </Button>
                        {hasQuickFilters && (
                            <div className="ml-1">
                                <Button variant="ghost-blue" onPress={clearFilters}>
                                    <Icon color="blueOne" name="x-close" />
                                </Button>
                            </div>
                        )}
                    </div>

                    <CvtTable downloadingIds={downloadingIds} onDownload={handleDownloadReport} />
                </div>
            </div>

            <ToastContainer
                position="top-right"
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                transition={Bounce}
            />

            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name="checkmark" size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant="success"
                position="top-center"
            />

            {toggledOverlays.escalationQuickFilters && (
                <CVTQuickFilters clearFilters={clearFilters} onClose={toggleQuickFilterPanel} />
            )}
        </>
    );
};

export default CVTPage;
